<template>
    <generic-page>
        <div class="d-flex align-items-center justify-content-between">
            <b-btn variant="link" size="lg" class="pl-0" to="/spotlight">
                <font-awesome-icon size="lg" icon="fa-light fa-chevron-left" />
                Spotlight
            </b-btn>
            <b-btn @click="createPost" variant="primary" pill>
                <font-awesome-icon icon="fa-light fa-plus" class="mr-1" />
                New Video
            </b-btn>
        </div>

        <h5 class="mt-3 mb-0">Video Studio</h5>
        <div class="text-secondary small mb-3">
            Manage, edit, and promote your videos
        </div>

        <div v-if="creatorVideos === null">
            <generic-loading />
        </div>
        <div v-else-if="creatorVideos.length === 0" class="empty-state">
            <font-awesome-icon
                icon="fa-duotone fa-video"
                size="3x"
                class="text-secondary mb-3"
            />
            <h6>No videos yet</h6>
            <div class="text-secondary small mb-3">
                Upload your first video to start earning
            </div>
            <b-btn @click="createPost" variant="primary" pill>
                <font-awesome-icon icon="fa-light fa-plus" class="mr-1" />
                Create Video
            </b-btn>
        </div>
        <div v-else>
            <div class="video-grid">
                <div
                    v-for="video in creatorVideos"
                    :key="video.id"
                    class="video-card"
                    @click="
                        $router.push({
                            name: 'creatorVideoEdit',
                            params: { id: video.id },
                        })
                    "
                >
                    <div class="video-card-preview">
                        <img
                            v-if="video.previewUrl || video.thumbnailUrl"
                            :src="video.previewUrl || video.thumbnailUrl"
                            class="video-card-img"
                        />
                        <div v-else class="video-card-placeholder">
                            <font-awesome-icon
                                icon="fa-duotone fa-video"
                                size="2x"
                                class="text-secondary"
                            />
                        </div>
                        <div v-if="video.duration" class="video-card-duration">
                            {{ formatDuration(video.duration) }}
                        </div>
                        <b-badge
                            :variant="getStatusVariant(video)"
                            pill
                            class="video-card-status"
                        >
                            {{ getStatusLabel(video) }}
                        </b-badge>
                    </div>
                    <div class="video-card-info">
                        <div
                            v-if="video.message"
                            class="video-card-message text-truncate"
                        >
                            {{ video.message }}
                        </div>
                        <div class="video-card-meta">
                            <span>
                                {{ video.publishDateTerse || "Draft" }}
                            </span>
                            <coin-badge
                                v-if="video.totalCreditsEarned"
                                :amount="video.totalCreditsEarned"
                                class="ml-auto"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <generic-pagination
                @next="nextPage"
                @previous="previousPage"
                :hasMorePages="hasMorePages"
                :page="page"
            />
        </div>
    </generic-page>
</template>

<style scoped>
.empty-state {
    text-align: center;
    padding: 48px 16px;
}

.video-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
}

.video-card {
    cursor: pointer;
}

.video-card-preview {
    position: relative;
    aspect-ratio: 9 / 16;
    overflow: hidden;
    border-radius: 8px;
    background: #1a1a1a;
}

.video-card-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
}

.video-card-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.video-card-duration {
    position: absolute;
    bottom: 6px;
    right: 6px;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    font-size: 0.65rem;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
}

.video-card-status {
    position: absolute;
    top: 6px;
    left: 6px;
    font-size: 0.6rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.video-card-info {
    padding: 6px 2px;
}

.video-card-message {
    font-size: 0.8rem;
    color: #e2e8f0;
    line-height: 1.3;
}

.video-card-meta {
    display: flex;
    align-items: center;
    font-size: 0.7rem;
    color: #94a3b8;
    margin-top: 2px;
}
</style>

<script>
import { mapState } from "vuex";

import GenericPagination from "@/components/utility/GenericPagination";

export default {
    created() {
        this.$store.dispatch("creatorVideo/refresh");
    },
    computed: {
        ...mapState("creatorVideo", [
            "creatorVideos",
            "hasMorePages",
            "page",
        ]),
    },
    methods: {
        createPost() {
            this.$store.dispatch("creatorVideo/create");
        },
        nextPage() {
            this.$store.dispatch("creatorVideo/nextPage");
        },
        previousPage() {
            this.$store.dispatch("creatorVideo/previousPage");
        },
        formatDuration(d) {
            const min = Math.floor(d / 60);
            const sec = d % 60;
            return `${min}:${String(sec).padStart(2, "0")}`;
        },
        getStatusLabel(video) {
            if (video.approved) return "Live";
            if (video.published) return "In Review";
            if (video.processed) return "Draft";
            return "Processing";
        },
        getStatusVariant(video) {
            if (video.approved) return "success";
            if (video.published) return "warning";
            if (video.processed) return "secondary";
            return "info";
        },
    },
    components: {
        GenericPagination,
    },
};
</script>
